import React, {FC} from 'react';
import {connect} from 'react-redux';

import {ResolveStateProps} from '../../../../../../libs/shared/core/src/store/helpers/commonReselectHelpers';
import {ViewerPanelId} from '../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {createSelector, makeMapStateToProps} from '../../../../../../srcCommon/helpers/reselectHelpers';
import {
  makeGetAttachments,
  makeGetAuthorFromComment,
  makeGetComment,
  makeGetCommentHighlightAnnotations,
  makeGetIsFromUser,
  makeGetReactionsVisualProps,
  makeGetReferencedActivity,
  makeGetReferencedAuthor,
  makeGetReferencedComment,
  makeGetReferencedRecipient,
} from '../../../../../../srcCommon/inboxes/viewer/activity/comment/commentViewerSelectors';
import {CommentActivityModel} from '../../../../../models/api/activityModel';
import {AppState} from '../../../../../store/appState';
import {getConversations} from '../../../../../store/data/dataSelectors';
import {getMentionableAliases} from '../../../../../store/data/teammates/mentionTeammatesSelectors';
import {getUserAlias, getUserId} from '../../../../../store/data/teammates/teammatesSelectors';
import {
  ConversationSummaryCommentClickHandler,
  ConversationSummaryCommentViewer,
} from './conversationSummaryCommentViewer';

/*
 * Props.
 */

export interface ConversationSummaryCommentViewerContainerPublicProps {
  panelId: ViewerPanelId;
  activity: CommentActivityModel;
  onJumpClick: ConversationSummaryCommentClickHandler;
  onPinClick: ConversationSummaryCommentClickHandler;
  onUnpinClick: ConversationSummaryCommentClickHandler;
}

function getActivityId(_state: AppState, props: ConversationSummaryCommentViewerContainerPublicProps) {
  return props.activity.id;
}

function makeGetConversationId(getComment: ReturnType<typeof makeGetComment>) {
  return createSelector([getComment], (comment) => comment?.conversationId);
}

function makeGetNamespace(getConversationId: ReturnType<typeof makeGetConversationId>) {
  return createSelector([getConversationId, getConversations], (conversationId, conversations) =>
    conversationId ? conversations[conversationId]?.namespace : undefined,
  );
}

type ConversationSummaryCommentViewerStateProps = ResolveStateProps<typeof mapStateToProps>;

type ConversationSummaryCommentViewerStatePropsContainerComponentProps =
  ConversationSummaryCommentViewerContainerPublicProps & ConversationSummaryCommentViewerStateProps;

const ConversationSummaryCommentViewerComponent: FC<
  ConversationSummaryCommentViewerStatePropsContainerComponentProps
> = (props) => {
  if (!props.comment || !props.author) {
    return null;
  }
  return (
    <ConversationSummaryCommentViewer
      panelId={props.panelId}
      activity={props.activity}
      comment={props.comment}
      author={props.author}
      referencedComment={props.referencedComment}
      referencedAuthor={props.referencedAuthor}
      referencedRecipient={props.referencedRecipient}
      attachments={props.attachments}
      annotations={props.annotations}
      reactions={props.reactions}
      userId={props.userId}
      userAlias={props.userAlias}
      mentionableAliases={props.mentionableAliases}
      onJumpClick={props.onJumpClick}
      onPinClick={props.onPinClick}
      onUnpinClick={props.onUnpinClick}
      namespace={props.namespace}
    />
  );
};

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<ConversationSummaryCommentViewerContainerPublicProps>()(() => {
  const getComment = makeGetComment();
  const getReferencedActivity = makeGetReferencedActivity(getComment);
  const getAuthor = makeGetAuthorFromComment(getComment);

  return {
    userId: getUserId,
    comment: getComment,
    author: getAuthor,
    referencedAuthor: makeGetReferencedAuthor(getReferencedActivity),
    referencedComment: makeGetReferencedComment(getReferencedActivity),
    referencedRecipient: makeGetReferencedRecipient(getReferencedActivity),
    attachments: makeGetAttachments(getComment),
    reactions: makeGetReactionsVisualProps(getComment),
    isFromUser: makeGetIsFromUser(getComment),
    mentionableAliases: getMentionableAliases,
    userAlias: getUserAlias,
    namespace: makeGetNamespace(makeGetConversationId(getComment)),
    annotations: makeGetCommentHighlightAnnotations(getActivityId),
  };
});

export const ConversationSummaryCommentViewerContainer = connect(mapStateToProps)(
  ConversationSummaryCommentViewerComponent,
);
